import React, { useState, useEffect } from "react";
import { changeAdminCredentials, loginAdmin } from "../../api/api";
import { useNavigate, useLocation } from "react-router-dom";
import "./Login.css";

function Login() {
  const [authMode, setAuthMode] = useState("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [oldUsername, setOldUsername] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(""); // 'success' or 'error'
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const popupTypeFromState = location.state?.popupType;
    const popupMessageFromState = location.state?.popupMessage;

    if (popupTypeFromState && popupMessageFromState) {
      setPopupType(popupTypeFromState);
      setSuccessMessage(popupTypeFromState === "success" ? popupMessageFromState : "");
      setError(popupTypeFromState === "error" ? popupMessageFromState : "");
      setShowPopup(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setShowPopup(false);
    setIsSubmitting(true);

    try {
      // Always send role: 'superadmin' in login
      const { data } = await loginAdmin({ username, password, role: "superadmin" });
      localStorage.setItem("token", data.token);
      if (data.role) {
        localStorage.setItem("role", data.role);
      }
      setSuccessMessage(data.message || "Login successful");
      setPopupType("success");
      setShowPopup(true);
      setTimeout(() => {
        navigate("/super-admin", { replace: true });
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.error || "Login failed");
      setPopupType("error");
      setShowPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCredentialUpdate = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setShowPopup(false);
    setIsSubmitting(true);

    try {
      // Always send role: 'superadmin' in credential update
      const { data } = await changeAdminCredentials({
        oldUsername,
        oldPassword,
        newUsername,
        newPassword,
        role: "superadmin"
      });

      setSuccessMessage(data.message || "Credentials updated successfully");
      setPopupType("success");
      setShowPopup(true);
      setUsername(newUsername);
      setPassword(newPassword);
      setOldUsername("");
      setOldPassword("");
      setNewUsername("");
      setNewPassword("");
      setShowOldPassword(false);
      setShowNewPassword(false);
      setAuthMode("login");
    } catch (err) {
      setError(err.response?.data?.error || "Credential update failed");
      setPopupType("error");
      setShowPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  // Auto-hide popup after 3 seconds
  useEffect(() => {
    if (showPopup) {
      const timer = setTimeout(() => {
        setShowPopup(false);
        setSuccessMessage("");
        setError("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showPopup]);

  return (
    <div className="login-page">
      {/* Side Popup Message - always above all content */}
      {showPopup && (
        <div
          style={{
            position: "fixed",
            top: 30,
            right: 30,
            minWidth: 250,
            zIndex: 120000,
            background: popupType === "success" ? "#4BB543" : "#e74c3c",
            color: "#fff",
            padding: "16px 28px",
            borderRadius: 8,
            boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
            fontSize: 16,
            fontWeight: 500,
            letterSpacing: 0.2,
            transition: "all 0.3s"
          }}
        >
          {popupType === "success" ? successMessage : error}
        </div>
      )}
      <div className="login-shell">
        <section className="login-panel">
          <span className="login-kicker">Jobfind24 Admin</span>
          <h1 className="login-title">Secure control panel access for your publishing workflow.</h1>
          <p className="login-copy">
            Sign in to manage homepage updates, job listings, admit cards, results, footer links, and contact records from one protected admin workspace.
          </p>

          <div className="login-points">
            <div className="login-point">
              <div className="login-point-badge">01</div>
              <div>
                <strong>Protected admin access</strong>
                <span>Only verified admin credentials can open the dashboard and use write actions.</span>
              </div>
            </div>

            <div className="login-point">
              <div className="login-point-badge">02</div>
              <div>
                <strong>Direct content control</strong>
                <span>Update latest jobs, results, admission details, and legal/footer sections without touching code.</span>
              </div>
            </div>

            <div className="login-point">
              <div className="login-point-badge">03</div>
              <div>
                <strong>Cloud-ready session flow</strong>
                <span>Login works the same way after deployment using your live domain’s admin route.</span>
              </div>
            </div>
          </div>
        </section>

        <section className="login-card">
          <div className="login-card-header">
            <div className="login-mode-switch" role="tablist" aria-label="Admin auth actions">
              <button
                type="button"
                className={`login-mode-button ${authMode === "login" ? "is-active" : ""}`}
                onClick={() => {
                  setAuthMode("login");
                  setError("");
                  setSuccessMessage("");
                }}
              >
                Login
              </button>
              <button
                type="button"
                className={`login-mode-button ${authMode === "update" ? "is-active" : ""}`}
                onClick={() => {
                  setAuthMode("update");
                  setError("");
                  setSuccessMessage("");
                }}
              >
                Update Credentials
              </button>
            </div>

            <h2>{authMode === "login" ? "Admin Login" : "Update Username & Password"}</h2>
            <p>
              {authMode === "login"
                ? "Enter your administrator credentials to continue to the protected control panel."
                : "Verify your old username and password first, then set the new login details for this admin account."}
            </p>
          </div>

          <form onSubmit={authMode === "login" ? handleLogin : handleCredentialUpdate} className="login-form">
            {error ? <div className="login-error">{error}</div> : null}
            {successMessage ? <div className="login-success">{successMessage}</div> : null}

            {authMode === "login" ? (
              <>
                <div className="login-field">
                  <label htmlFor="admin-username">Username</label>
                  <input
                    id="admin-username"
                    placeholder="Enter admin username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    required
                  />
                </div>

                <div className="login-field">
                  <label htmlFor="admin-password">Password</label>
                  <div className="login-password-wrap">
                    <input
                      id="admin-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                    <button
                      type="button"
                      className="login-password-toggle"
                      onClick={() => setShowPassword((current) => !current)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                <button className="login-submit" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Logging in..." : "Open Admin Panel"}
                </button>
              </>
            ) : (
              <>
                <div className="login-form-grid">
                  <div className="login-field">
                    <label htmlFor="old-admin-username">Old Username</label>
                    <input
                      id="old-admin-username"
                      placeholder="Enter current username"
                      value={oldUsername}
                      onChange={(e) => setOldUsername(e.target.value)}
                      autoComplete="username"
                      required
                    />
                  </div>

                  <div className="login-field">
                    <label htmlFor="old-admin-password">Old Password</label>
                    <div className="login-password-wrap">
                      <input
                        id="old-admin-password"
                        type={showOldPassword ? "text" : "password"}
                        placeholder="Enter current password"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        className="login-password-toggle"
                        onClick={() => setShowOldPassword((current) => !current)}
                        aria-label={showOldPassword ? "Hide old password" : "Show old password"}
                      >
                        {showOldPassword ? "Hide" : "Show"}
                      </button>
                    </div>
                  </div>

                  <div className="login-field">
                    <label htmlFor="new-admin-username">New Username</label>
                    <input
                      id="new-admin-username"
                      placeholder="Enter new username"
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      autoComplete="username"
                      required
                    />
                  </div>

                  <div className="login-field">
                    <label htmlFor="new-admin-password">New Password</label>
                    <div className="login-password-wrap">
                      <input
                        id="new-admin-password"
                        type={showNewPassword ? "text" : "password"}
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoComplete="new-password"
                        required
                      />
                      <button
                        type="button"
                        className="login-password-toggle"
                        onClick={() => setShowNewPassword((current) => !current)}
                        aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                      >
                        {showNewPassword ? "Hide" : "Show"}
                      </button>
                    </div>
                  </div>
                </div>

                <input
                  className="login-hidden-submit"
                  tabIndex={-1}
                  aria-hidden="true"
                  readOnly
                  value=""
                />

                <button className="login-submit" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Updating..." : "Update Credentials"}
                </button>

                <div className="login-helper-text">
                  After a successful update, the login form will be pre-filled with your new username and password.
                </div>
              </>
            )}
          </form>

          <div className="login-note">
            Use this page only for authorized administration. If you deploy the project, the same login flow will be available on your live domain at <strong>/login</strong>.
          </div>
        </section>
      </div>
    </div>
  );
}

export default Login;
